import { Component, State, h } from '@stencil/core';
import { getStoreInformation } from './utils';
import { isBrowser } from '../../constants/constants';

declare global {
  interface Window {
    dataLayer: any;
    aa: any;
  }
}

interface Store {
  name: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    region: string;
    postalCode: string;
  };
  phone: string;
  landingPageUrl: string;
}

@Component({
  tag: 'ci-local-store',
  styleUrl: 'ci-local-store.scss',
  scoped: true,
})
export class CiLocalStore {
  @State() store: Store = null;

  async componentDidLoad() {
    if (isBrowser) {
      const storeInformation = sessionStorage.getItem('storeInformation');
      if (storeInformation !== null) {
        this.store = JSON.parse(storeInformation);
      } else {
        const storeInformation = await getStoreInformation();
        if (storeInformation !== null) {
          this.fireAnalyticsEvent(storeInformation);
          sessionStorage.setItem('storeInformation', JSON.stringify(storeInformation));
        }
        this.store = storeInformation;
      }
    }
  }

  fireAnalyticsEvent = (store: Store) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'local_store',
      location: `${store.address.city}, ${store.address.region}`,
    });
    return;
  };

  storesDropdown = () => {
    const { line1, line2, city, region, postalCode } = this.store.address;
    return (
      <div class="ghf-localStore ghf-localStore--desktop">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M11.3445 12.7958C10.131 14.236 8.60164 15.5 8 15.5C7.39836 15.5 5.869 14.236 4.65552 12.7958C3.00864 10.8412 2 8.69108 2 6.5C2 3.18629 4.68629 0.5 8 0.5C11.3137 0.5 14 3.18629 14 6.5C14 8.69108 12.9914 10.8412 11.3445 12.7958ZM7.49332 13.5156C7.71583 13.6996 8 13.9058 8 13.9058C8 13.9058 8.31603 13.6732 8.50668 13.5156C9.05991 13.0584 9.65442 12.4736 10.1974 11.8292C11.637 10.1207 12.5 8.28098 12.5 6.5C12.5 4.01472 10.4853 2 8 2C5.51472 2 3.5 4.01472 3.5 6.5C3.5 8.28098 4.36302 10.1207 5.80262 11.8292C6.34558 12.4736 6.94009 13.0584 7.49332 13.5156ZM5.75 6.5C5.75 5.25736 6.75736 4.25 8 4.25C9.24264 4.25 10.25 5.25736 10.25 6.5C10.25 7.74264 9.24264 8.75 8 8.75C6.75736 8.75 5.75 7.74264 5.75 6.5ZM8.75 6.5C8.75 6.08579 8.41421 5.75 8 5.75C7.58579 5.75 7.25 6.08579 7.25 6.5C7.25 6.91421 7.58579 7.25 8 7.25C8.41421 7.25 8.75 6.91421 8.75 6.5Z" fill="white"/>
        </svg>
        <span>
          Visit Your Store:{' '}
          <span class="ghf-localStore-hover">
            <u>{this.store.name}</u>
          </span>
        </span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="Icons/Arrow Down">
            <path id="&#240;&#159;&#142;&#168; Fill Color" fill-rule="evenodd" clip-rule="evenodd" d="M12.1949 5.52876C12.4553 5.26841 12.8774 5.26841 13.1377 5.52876C13.3981 5.78911 13.3981 6.21122 13.1377 6.47157L8.47108 11.1382C8.21073 11.3986 7.78862 11.3986 7.52827 11.1382L2.8616 6.47157C2.60125 6.21122 2.60125 5.78911 2.8616 5.52876C3.12195 5.26841 3.54406 5.26841 3.80441 5.52876L7.99967 9.72402L12.1949 5.52876Z" fill="white"/>
          </g>
        </svg>

        <aside id="localStore-tooltip" class="ghf-localStore-tooltip">
          <span class="ghf-localStore-tooltip-address">
            {line1} <br />
            {line2 && (
              <span>
                {line2} <br />
              </span>
            )}
            {city}, {region} {postalCode}
          </span>
          <div class="ghf-localStore-tooltip-actions">
            <a
              href={`tel:${this.store.phone}`}
              class="js-ciHeader-track"
              data-section="stores phone"
              data-link={`stores phone ${this.store.phone}`}
            >
              {this.store.phone}
            </a>
            <a
              href="https://locations.customink.com"
              class="js-ciHeader-track"
              data-section="stores change"
              data-link="stores change"
            >
              Change Store
            </a>
            <a
              href={this.store.landingPageUrl}
              class="js-ciHeader-track"
              data-section="stores appointment"
              data-link="stores appointment"
            >
              Book an appointment
            </a>
          </div>
        </aside>
      </div>
    );
  };

  render() {
    return (
      <div>
        <a class="ghf-localStore ghf-localStore--mobile js-ciHeader-track" href="https://locations.customink.com/"  data-section="stores" data-link="stores">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M11.3445 12.7958C10.131 14.236 8.60164 15.5 8 15.5C7.39836 15.5 5.869 14.236 4.65552 12.7958C3.00864 10.8412 2 8.69108 2 6.5C2 3.18629 4.68629 0.5 8 0.5C11.3137 0.5 14 3.18629 14 6.5C14 8.69108 12.9914 10.8412 11.3445 12.7958ZM7.49332 13.5156C7.71583 13.6996 8 13.9058 8 13.9058C8 13.9058 8.31603 13.6732 8.50668 13.5156C9.05991 13.0584 9.65442 12.4736 10.1974 11.8292C11.637 10.1207 12.5 8.28098 12.5 6.5C12.5 4.01472 10.4853 2 8 2C5.51472 2 3.5 4.01472 3.5 6.5C3.5 8.28098 4.36302 10.1207 5.80262 11.8292C6.34558 12.4736 6.94009 13.0584 7.49332 13.5156ZM5.75 6.5C5.75 5.25736 6.75736 4.25 8 4.25C9.24264 4.25 10.25 5.25736 10.25 6.5C10.25 7.74264 9.24264 8.75 8 8.75C6.75736 8.75 5.75 7.74264 5.75 6.5ZM8.75 6.5C8.75 6.08579 8.41421 5.75 8 5.75C7.58579 5.75 7.25 6.08579 7.25 6.5C7.25 6.91421 7.58579 7.25 8 7.25C8.41421 7.25 8.75 6.91421 8.75 6.5Z" fill="white"/>
          </svg>
          Stores
        </a>
        {this.store !== null ? this.storesDropdown() : ''}
      </div>
    );
  }
}
